import { defineStore } from "pinia";
import { useAPIStore } from "./APIStore";
import LastSync from "@/types/LastSync";

// Polling retries every updateInterval regardless of whether the last
// attempt failed, so a broken endpoint would otherwise re-toast on every
// tick. Once it has failed, later attempts within this window stay silent;
// this is long enough to cover several ticks without going quiet forever.
const REPEAT_FAILURE_SILENCE_MS = 30_000

export const useSyncStore = defineStore('syncStore', {
    state: () => ({
        syncInfo: {} as Record<string, LastSync>,
        lastUpdate: null as number | null,
        lastFailureAt: null as number | null,
        updateInterval: 3000, // 3 seconds
        isUpdating: false,
        newData: false,
        lastDataIsLive: false,
        pendingUpdatePromise: null as Promise<Record<string, LastSync>> | null
    }),
    getters: {
        getSyncInfo: (state) => (syncType: string) => state.syncInfo[syncType],
        getAllSyncInfo: (state) => state.syncInfo,
    },
    actions: {
        async updateSyncInfo(force=false, cacheSource=null) {
            const now = Date.now();

            if(cacheSource == "live"){
                this.lastDataIsLive = true
            }
            
            // If there's already an update in progress, return its promise
            if (this.pendingUpdatePromise) {
                return this.pendingUpdatePromise;
            }

            // If we have recent data and no update is needed, return current data
            if (!force && this.lastUpdate && (now - this.lastUpdate) < this.updateInterval) {
                return this.syncInfo;
            }

            this.pendingUpdatePromise = (async () => {
                const recentlyFailed = this.lastFailureAt != null && (now - this.lastFailureAt) < REPEAT_FAILURE_SILENCE_MS
                try {
                    this.isUpdating = true;
                    const APIStore = useAPIStore();
                    const allSyncInfo = await APIStore.getAllSyncInfo(recentlyFailed ? { hideToast: true } : {});
                    this.syncInfo = allSyncInfo;
                    this.lastUpdate = now;
                    this.lastFailureAt = null;
                    return allSyncInfo;
                } catch (error) {
                    console.error('Error updating sync info:', error);
                    // record the attempt even on failure, so a broken endpoint
                    // is retried at most once per interval instead of on every
                    // call that races in before the next tick
                    this.lastUpdate = now;
                    this.lastFailureAt = now;
                    return this.syncInfo;
                } finally {
                    this.isUpdating = false;
                    this.pendingUpdatePromise = null;
                }
            })();

            return this.pendingUpdatePromise;
        }
    }
}); 